import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Palette, Mail, Receipt, Image as ImageIcon, Check, Sparkles, Sun, Moon, Smile, Bell, Shield, ListChecks } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { THEME_PRESETS, applyThemePreset } from "@/lib/themes";
import AvatarStudio from "@/pages/AvatarStudio";
import FieldHelp from "@/components/FieldHelp";
import PushSettings from "@/components/PushSettings";
import AccountSettings from "@/components/AccountSettings";
import NdisCodesSettings from "@/components/NdisCodesSettings";

const PATTERN_PRESETS = [
  { name: "None", url: "" },
  { name: "Soft Waves", url: "https://images.unsplash.com/photo-1649783465453-afa79b5c597a?w=1600&q=80" },
  { name: "Pastel Bubbles", url: "https://images.unsplash.com/photo-1697033300784-6c9d143a30e2?w=1600&q=80" },
  { name: "Sunset Sky", url: "https://images.unsplash.com/photo-1495616811223-4d98c6e9c869?w=1600&q=80" },
  { name: "Forest Path", url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=80" },
  { name: "Ocean", url: "https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=1600&q=80" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [s, setS] = useState({ theme: "light", theme_preset: "teal", background_url: "",
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", from_email: "", ato_km_rate: 0.88,
    pending_alerts_enabled: true });
  const [testEmail, setTestEmail] = useState("");
  const upd = (k) => (e) => setS((x) => ({ ...x, [k]: e?.target?.value ?? e }));

  useEffect(() => { (async () => {
    const { data } = await api.get("/settings"); setS((p) => ({ ...p, ...data }));
  })(); }, []);

  const save = async (extra) => {
    try {
      const payload = { ...s, ...(extra || {}) };
      const { data } = await api.put("/settings", payload);
      setS((p) => ({ ...p, ...data }));
      toast.success("Saved");
      document.documentElement.classList.toggle("dark", data.theme === "dark");
      applyThemePreset(data.theme_preset);
      applyBackground(data.background_url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyBackground = (url) => {
    if (url) {
      document.body.style.backgroundImage = `linear-gradient(hsl(var(--background)/0.86), hsl(var(--background)/0.86)), url(${url})`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundAttachment = "fixed";
    } else {
      document.body.style.backgroundImage = "";
    }
  };

  const pickPreset = async (id) => {
    setS((p) => ({ ...p, theme_preset: id }));
    applyThemePreset(id);
    await save({ theme_preset: id });
  };

  const pickPattern = async (url) => {
    setS((p) => ({ ...p, background_url: url }));
    applyBackground(url);
    await save({ background_url: url });
  };

  const test = async () => {
    try { await api.post("/settings/test-email", { to: testEmail }); toast.success("Test email sent"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Make the app yours — colours, backgrounds, email, tax.</p>
      </header>

      <Tabs defaultValue={user?.role === "client" ? "avatar" : "style"}>
        <TabsList className="rounded-full p-1 h-12 flex-wrap">
          {user?.role === "client" && <TabsTrigger value="avatar" className="rounded-full gap-1.5" data-testid="tab-avatar"><Smile className="w-4 h-4" /> Avatar</TabsTrigger>}
          <TabsTrigger value="style" className="rounded-full gap-1.5" data-testid="tab-style"><Palette className="w-4 h-4" /> Style</TabsTrigger>
          <TabsTrigger value="background" className="rounded-full gap-1.5" data-testid="tab-background"><ImageIcon className="w-4 h-4" /> Background</TabsTrigger>
          {user?.role === "client" && <TabsTrigger value="email" className="rounded-full gap-1.5" data-testid="tab-email"><Mail className="w-4 h-4" /> Email</TabsTrigger>}
          <TabsTrigger value="push" className="rounded-full gap-1.5" data-testid="tab-push"><Bell className="w-4 h-4" /> Push</TabsTrigger>
          {user?.role === "client" && <TabsTrigger value="tax" className="rounded-full gap-1.5" data-testid="tab-tax"><Receipt className="w-4 h-4" /> Tax</TabsTrigger>}
          {user?.role === "client" && <TabsTrigger value="ndis-codes" className="rounded-full gap-1.5" data-testid="tab-ndis-codes"><ListChecks className="w-4 h-4" /> NDIS codes</TabsTrigger>}
          <TabsTrigger value="account" className="rounded-full gap-1.5" data-testid="tab-account"><Shield className="w-4 h-4" /> Account</TabsTrigger>
        </TabsList>

        {user?.role === "client" && (
          <TabsContent value="avatar">
            <AvatarStudio />
          </TabsContent>
        )}

        <TabsContent value="style" className="space-y-4">
          <Card className="rounded-3xl p-6 border-2 space-y-5" data-testid="style-card">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-bold flex items-center gap-2">
                  {s.theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Dark mode
                </Label>
                <p className="text-xs text-muted-foreground">Easier on the eyes at night.</p>
              </div>
              <Switch checked={s.theme === "dark"} onCheckedChange={(v) => save({ theme: v ? "dark" : "light" })} data-testid="theme-toggle" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Colour theme</Label>
              <p className="text-xs text-muted-foreground mb-3">Pick a vibe — applies instantly.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="theme-presets">
                {THEME_PRESETS.map((p) => {
                  const active = s.theme_preset === p.id;
                  return (
                    <button key={p.id} type="button" onClick={() => pickPreset(p.id)}
                      data-testid={`preset-${p.id}`}
                      className={`relative rounded-2xl border-2 p-3 text-left transition-all card-lift ${active ? "border-client ring-4 ring-client/25" : "border-border hover:border-client/40"}`}
                      style={{ background: p.swatch[3] }}>
                      {active && <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-client text-white grid place-items-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></div>}
                      <div className="flex gap-1 mb-2">
                        {p.swatch.slice(0, 3).map((c, i) => (
                          <div key={i} className="w-6 h-6 rounded-full ring-2 ring-white" style={{ background: c }} />
                        ))}
                      </div>
                      <div className="font-display font-extrabold text-sm" style={{ color: p.swatch[0] }}>{p.name}</div>
                      <div className="text-[11px] text-foreground/70 leading-tight mt-0.5">{p.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="background">
          <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="background-card">
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider">Pick a background pattern</Label>
              <p className="text-xs text-muted-foreground mb-3">Or paste a custom image URL below.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PATTERN_PRESETS.map((p) => {
                  const active = (s.background_url || "") === p.url;
                  return (
                    <button key={p.name} type="button" onClick={() => pickPattern(p.url)}
                      data-testid={`bg-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`relative rounded-2xl border-2 overflow-hidden h-28 transition-all card-lift ${active ? "border-client ring-4 ring-client/25" : "border-border hover:border-client/40"}`}>
                      {p.url
                        ? <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full grid place-items-center text-muted-foreground bg-muted text-sm font-bold">No background</div>}
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-white text-xs font-bold">{p.name}</div>
                      {active && <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-client text-white grid place-items-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></div>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Custom background URL</Label>
              <div className="flex gap-2">
                <Input value={s.background_url || ""} onChange={upd("background_url")} placeholder="https://..." className="rounded-xl min-h-[48px]" data-testid="background-url" />
                <Button onClick={() => save()} className="rounded-full font-bold gap-1" style={{ background: "hsl(var(--client))" }} data-testid="save-bg-btn">
                  <Sparkles className="w-4 h-4" /> Apply
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {user?.role === "client" && (
          <TabsContent value="email">
            <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="email-card">
              <div className="rounded-2xl bg-client/8 border-2 border-client/20 px-4 py-3 flex items-start gap-3" data-testid="email-help-banner">
                <Sparkles className="w-5 h-5 text-client mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold">First time setting up email? It only takes 2 minutes.</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Step-by-step instructions for Gmail, Outlook and others — including the App password trick for Gmail.</p>
                </div>
                <a href="/help#email-setup" data-testid="email-help-link"
                   className="text-xs font-bold rounded-full bg-client text-white px-3 py-2 hover:opacity-90 whitespace-nowrap">
                  Show me how →
                </a>
              </div>
              <p className="text-sm text-muted-foreground">Connect your own email account so invoices and incident alerts are sent from you.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <F label="SMTP host" v={s.smtp_host} oc={upd("smtp_host")} ph="smtp.gmail.com" id="smtp-host" />
                <F label="SMTP port" v={s.smtp_port} oc={upd("smtp_port")} type="number" id="smtp-port" />
                <F label="SMTP user" v={s.smtp_user} oc={upd("smtp_user")} ph="you@gmail.com" id="smtp-user" />
                <F label="SMTP password (App Password)" v={s.smtp_password} oc={upd("smtp_password")} type="password" id="smtp-password" />
                <F label="From email" v={s.from_email} oc={upd("from_email")} ph="you@gmail.com" id="from-email" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => save()} className="rounded-full font-bold gap-1.5" style={{ background: "hsl(var(--client))" }} data-testid="save-email-btn"><Mail className="w-4 h-4" /> Save SMTP</Button>
                <div className="flex-1 flex gap-2 min-w-[260px]">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="recipient@example.com" className="rounded-xl min-h-[44px]" data-testid="test-email-input" />
                  <Button variant="outline" onClick={test} className="rounded-full font-bold border-2" data-testid="test-email-btn">Send test</Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Tip: Gmail users → enable 2FA, then create an App password at myaccount.google.com/apppasswords. Use port 587.</p>
              <div className="flex items-center justify-between pt-3 border-t mt-3" data-testid="pending-alerts-row">
                <div>
                  <Label className="text-base font-bold">Instant pending-approval alerts</Label>
                  <p className="text-xs text-muted-foreground">Email me the moment a worker creates a timesheet, vital, incident or anything else needing my approval.</p>
                </div>
                <Switch
                  checked={s.pending_alerts_enabled !== false}
                  onCheckedChange={(v) => save({ pending_alerts_enabled: v })}
                  data-testid="pending-alerts-toggle"
                />
              </div>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="push">
          <PushSettings />
        </TabsContent>

        {user?.role === "client" && (
          <TabsContent value="tax">
            <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="tax-card">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
                  ATO cents-per-km rate ($/km)
                  <FieldHelp text="The Australian Taxation Office cents-per-kilometre rate. Currently $0.88 for 2024-25. Used to calculate dollar claims for kilometre logs." learnMoreId="kilometres" />
                </Label>
                <Input value={s.ato_km_rate ?? ""} onChange={(e) => upd("ato_km_rate")({ target: { value: parseFloat(e.target.value) || 0 } })}
                       type="number" placeholder="0.88" className="rounded-xl min-h-[48px]" data-testid="field-ato-rate" />
              </div>
              <Button onClick={() => save()} className="rounded-full font-bold gap-1.5" style={{ background: "hsl(var(--client))" }} data-testid="save-tax-btn"><Receipt className="w-4 h-4" /> Save</Button>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="account">
          <AccountSettings />
        </TabsContent>

        {user?.role === "client" && (
          <TabsContent value="ndis-codes">
            <NdisCodesSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function F({ label, v, oc, type = "text", ph, id }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase font-bold tracking-wider">{label}</Label>
      <Input value={v ?? ""} onChange={oc} type={type} placeholder={ph} className="rounded-xl min-h-[48px]" data-testid={`field-${id}`} />
    </div>
  );
}
